import axios from 'axios';
import Cookies from 'js-cookie';
import { get, post, del } from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL;

export const login = async (email, password) => {

  // i've apparently got some duplication between this login and the login in authContext.js

  try {
    const response = await post(`${API_URL}/auth/login`, { email, password });
    //console.log('Login response:', response);
    if (response.token && response.userData) {
      ///console.log('Received token:', response.token);
      Cookies.set('token', response.token, { expires: 7 });
      localStorage.setItem('user', JSON.stringify(response.userData));
      localStorage.setItem('userCachedAt', Date.now().toString());
      return {
        success: true,
        userData: response.userData
      };
    }
    return { success: false, message: 'Login failed' };
  } catch (error) {
    console.error('Login error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'An error occurred' 
    };
  }
};

export const register = async (name, email, mobileNumber, password, plan) => {
  try {
    const response = await post(`${API_URL}/auth/register`, {
      name,
      email,
      mobileNumber,
      password,
      ...(plan ? { plan } : {})
    });

    //console.log('Registration response:', response); // Debug log

    if (response.token && response.userData) {
      //console.log('Received token:', response.token);
      Cookies.set('token', response.token, { expires: 7 });
      localStorage.setItem('user', JSON.stringify(response.userData));
      localStorage.setItem('userCachedAt', Date.now().toString());
      return {
        success: true,
        userData: response.userData
      };
    }
    return {
      success: false,
      message: response.message || 'Registration failed'
    };
  } catch (error) {
    console.error('Registration error:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred'
    };
  }
};

// Redeems the single-use code from the welcome WhatsApp link (/i/:code). Lead/provisioned
// accounts come back with requiresPasswordSet + a resetToken (caller routes them to the
// reset-password page); everyone else gets a normal session stored exactly like login().
export const loginWithCode = async (code) => {
  try {
    const response = await post(`${API_URL}/auth/token-login`, { code });

    if (response.requiresPasswordSet && response.resetToken) {
      return { success: true, requiresPasswordSet: true, resetToken: response.resetToken };
    }

    if (response.token && response.userData) {
      Cookies.set('token', response.token, { expires: 7 });
      localStorage.setItem('user', JSON.stringify(response.userData));
      localStorage.setItem('userCachedAt', Date.now().toString());
      return { success: true, requiresPasswordSet: false, userData: response.userData };
    }

    return { success: false, message: 'This link is invalid or has expired.' };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'This link is invalid or has expired.',
    };
  }
};

// Staff-assisted signup (via the secret /staff-signup?k=… link). Creates the customer account and
// returns a session as that customer so the staff member can complete onboarding on their behalf.
// The shared secret travels in the body and is validated server-side by StaffSignupSecretGuard.
// Referral-link signup (/r/<code>). Mirrors staffRegister; `use_login` means the email already
// has an account (no session issued, no re-attribution).
export const referralRegister = async ({ name, email, mobileNumber, code }) => {
  try {
    const response = await post(`${API_URL}/auth/referral-register`, { name, email, mobileNumber, code });
    if (response.token && response.userData) {
      Cookies.set('token', response.token, { expires: 7 });
      localStorage.setItem('user', JSON.stringify(response.userData));
      localStorage.setItem('userCachedAt', Date.now().toString());
      return { success: true, userData: response.userData };
    }
    if (response.status === 'use_login') {
      return { success: false, status: 'use_login' };
    }
    return { success: false, message: response.message || 'Failed to create the account' };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred',
    };
  }
};

export const staffRegister = async ({ name, email, mobileNumber, plan, secret }) => {
  try {
    const response = await post(`${API_URL}/auth/staff-register`, {
      name,
      email,
      mobileNumber,
      secret,
      ...(plan ? { plan } : {}),
    });
    if (response.token && response.userData) {
      // Set the auth cookie + cache here (like login/register); the page then calls the
      // AuthContext login() to update in-memory state before navigating.
      Cookies.set('token', response.token, { expires: 7 });
      localStorage.setItem('user', JSON.stringify(response.userData));
      localStorage.setItem('userCachedAt', Date.now().toString());
      return { success: true, userData: response.userData };
    }
    return { success: false, message: response.message || 'Failed to create the account' };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred',
    };
  }
};

export const logout = () => {
  Cookies.remove('token');
};

// Tutors only: swaps a still-valid login for a fresh 7-day one (AuthContext calls it while they're active).
export const renewLogin = async () => {
  const { token } = await post('/auth/renew', {}, true);
  Cookies.set('token', token, { expires: 7 });
};

export const verifyToken = async (token) => {
  try {
    //console.log('Verifying token:', token); // Log the token being sent
    const response = await axios.get(`${API_URL}/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    //console.log('Verification response:', response.data); // Log the response
    return response.data;
  } catch (error) {
    console.error('Token verification error:', error.response ? error.response.data : error.message);
    throw error;
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await post(`${API_URL}/auth/forgot-password`, { email });
    return { success: true, message: response.message };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      message: error.response?.data?.message || 'An error occurred'
    };
  }
};

// Step 2 of the OTP reset: exchange the 6-digit code for a short-lived reset token, which the
// existing /reset-password/:token page consumes.
export const verifyResetCode = async (email, code) => {
  try {
    const response = await post(`${API_URL}/auth/verify-reset-code`, { email, code });
    return { success: true, resetToken: response.resetToken };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      message: error.response?.data?.message || 'An error occurred',
    };
  }
};

export const resetPassword = async (token, newPassword) => {
  try {
    const response = await post(`${API_URL}/auth/reset-password`, {
      token,
      newPassword
    });
    return { success: true, message: response.message };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      message: error.response?.data?.message || 'An error occurred'
    };
  }
};
